// AvisoVersionNueva.jsx
//
// Avisa que hay una versión nueva de la web desplegada. Cada deploy borra el
// main.<hash>.js anterior, y una pestaña abierta desde antes sigue corriendo
// el código viejo contra el backend nuevo sin enterarse.
//
// Cómo detecta: /asset-manifest.json (lo genera CRA y el Caddyfile lo sirve
// con no-cache) dice en files["main.js"] cuál es el bundle vigente. Si no es
// el que está corriendo esta pestaña, hay versión nueva. Se mira al arrancar,
// cada 5 minutos y al volver a la pestaña.
//
// No recarga solo: el aviso ofrece "Recargar" y deja seguir, para no perder un
// formulario a medio llenar. En `npm start` no hay main.<hash>.js y no hace nada.
//
// No tapa nada: mientras se ve, el body suma abajo un margen de su alto, así
// el final de la página (el "Enviar solicitud" del formulario, por ejemplo)
// se puede subir por encima del aviso. Y va con zIndex 150, abajo de todos los
// modales (los propios usan 200 o más, MUI 1200 o más): un modal abierto lo tapa.
//
// Si el manifest falla (sin red, 404, HTML, JSON sin files["main.js"]) no
// aparece y no tira errores; se reintenta en la próxima vuelta. Lo único que
// queda es lo que el navegador registra solo de un pedido fallido (404 o sin
// red), que no sale de este código.

import { useEffect, useRef, useState } from 'react';

const font = 'Montserrat, sans-serif';
const CADA_MS = 5 * 60 * 1000;

const bundleCorriendo = () => {
  const s = [...document.scripts].find((x) => /\/static\/js\/main\.[0-9a-f]+\.js$/.test(x.src));
  return s ? new URL(s.src).pathname : null;
};

export default function AvisoVersionNueva() {
  const [hayNueva, setHayNueva] = useState(false);
  const [oculto, setOculto]     = useState(false);
  const caja = useRef(null);
  const visible = hayNueva && !oculto;

  useEffect(() => {
    const actual = bundleCorriendo();
    if (!actual) return undefined;
    let cortado = false;

    const revisar = async () => {
      if (cortado || document.visibilityState === 'hidden') return;
      try {
        const res = await fetch(`/asset-manifest.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const vigente = (await res.json())?.files?.['main.js'];
        if (typeof vigente === 'string' && vigente !== actual) {
          cortado = true;
          setHayNueva(true);
        }
      } catch {
        // Sin red o manifest roto: se vuelve a intentar en la próxima vuelta.
      }
    };

    revisar();
    const t = setInterval(revisar, CADA_MS);
    const alVolver = () => { if (document.visibilityState === 'visible') revisar(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => { cortado = true; clearInterval(t); document.removeEventListener('visibilitychange', alVolver); };
  }, []);

  useEffect(() => {
    if (!visible || !caja.current) return undefined;
    const previo = document.body.style.paddingBottom;
    const ajustar = () => { if (caja.current) document.body.style.paddingBottom = `${caja.current.offsetHeight + 32}px`; };
    ajustar();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(ajustar) : null;
    if (ro) ro.observe(caja.current);
    return () => { if (ro) ro.disconnect(); document.body.style.paddingBottom = previo; };
  }, [visible]);

  if (!visible) return null;

  return (
    <div ref={caja} role="status" style={{
      position: 'fixed', left: 16, right: 16, bottom: 16, zIndex: 150,
      maxWidth: 520, margin: '0 auto',
      background: '#163244', color: '#fff', borderRadius: 14,
      border: '1.5px solid rgba(231,164,50,0.6)',
      boxShadow: '0 8px 30px rgba(22,50,68,0.35)', padding: '14px 16px',
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontFamily: font,
    }}>
      <div style={{ flex: '1 1 220px', fontSize: 13, lineHeight: 1.5 }}>
        <div style={{ fontWeight: 800, marginBottom: 2 }}>Hay una versión nueva de Fixo</div>
        <div style={{ color: 'rgba(255,255,255,0.75)' }}>Recargá cuando termines lo que estás haciendo.</div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => setOculto(true)} style={{ background: 'transparent', color: 'rgba(255,255,255,0.8)', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: 50, padding: '9px 16px', fontFamily: font, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
          Más tarde
        </button>
        <button onClick={() => window.location.reload()} style={{ background: '#E7A432', color: '#163244', border: 'none', borderRadius: 50, padding: '9px 18px', fontFamily: font, fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
          Recargar
        </button>
      </div>
    </div>
  );
}
